import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {FONT, SIZE} from '../../../../../styles/foundation/typography'
import {SUBTLE, PRIMARY_BLUE, BORDER, withOpacity} from '../../../../../styles/foundation/colors'
import {volumetric} from '../state/packingModel'
import {toTriple} from '../../weightMath'

/** The reference side the drawing is scaled against, in centimetres. */
const REF_CM = 60
/** Pixels that reference side is drawn at. */
const CARTON_PX = 210
/** Smallest drawing we will render, so a 5 cm box is still legible. */
const MIN_PX = 46
const PAD = 14
/**
 * The side length, in cm, below which `MIN_PX` stops the drawing from
 * shrinking any further (`MIN_PX * REF_CM / CARTON_PX`). Below this, a 5 cm
 * cube and a 13 cm cube draw identically — the caption says so rather than
 * promising growth the drawing cannot deliver.
 */
const MIN_DRAWN_CM = (MIN_PX * REF_CM) / CARTON_PX

/**
 * Frame geometry. Deliberately a local copy of the kit's arithmetic rather
 * than an import of `components/CartonSVG.js`: the wrapper below has to size
 * itself from the same numbers the SVG lays out with, and a shared component
 * whose internals could drift is exactly the coupling the section ruling
 * pushed back on.
 */
function frame(triple, max) {
  const [l, b, h] = triple
  const big = Math.max(l, b, h) || 1
  const s = (max * 0.62) / big
  const W = l * s
  const H = h * s
  const D = b * s * 0.5
  return {W, H, D, w: W + D + PAD * 2, ht: H + D + PAD * 2}
}

/**
 * The MEASURED carton — Packing's own drawing, and the one place in the module
 * where the outline is genuinely to scale.
 *
 * `max` is derived from the biggest side (`CARTON_PX * big / REF_CM`), and the
 * geometry then divides by that same biggest side. The two cancel: the scale
 * factor lands at a constant `CARTON_PX * 0.62 / REF_CM` pixels per centimetre,
 * so typing 40 into a box that read 30 makes the drawing visibly bigger. That
 * growth is the entire point — it is editing feedback, the confirmation that
 * the number just typed landed on the right side of the carton.
 *
 * This is why it is NOT the shared `components/CartonSVG.js`, which normalises
 * the largest dimension away and conveys shape only. The two rules are
 * incompatible; they live in two files so neither can be reached by accident.
 *
 * Note the aria-label here reads "Carton drawn to scale" — the shared kit's
 * "Approximate carton" would contradict the caption underneath, which tells
 * the merchant in as many words that the outline tracks their numbers.
 */
function MeasuredCarton({dims, max}) {
  const [l, b, h] = dims
  const {W, H, D, w, ht} = frame(dims, max)

  return (
    <svg
      viewBox={`0 0 ${w} ${ht}`}
      width='100%'
      height={Math.min(ht, max)}
      role='img'
      aria-label={`Carton drawn to scale, ${l} by ${b} by ${h} centimetres`}
    >
      <g transform={`translate(${PAD},${PAD + D})`}>
        <polygon
          points={`0,0 ${D},${-D} ${W + D},${-D} ${W},0`}
          fill={withOpacity(PRIMARY_BLUE, 0.28)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        <polygon
          points={`${W},0 ${W + D},${-D} ${W + D},${H - D} ${W},${H}`}
          fill={withOpacity(PRIMARY_BLUE, 0.14)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        <rect
          x='0' y='0' width={W} height={H}
          fill={withOpacity(PRIMARY_BLUE, 0.08)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        <line x1={W * 0.5} y1='0' x2={W * 0.5} y2={H} stroke={BORDER} strokeWidth='2' />
      </g>
    </svg>
  )
}

/**
 * The measured drawing plus the caption that states its scaling rule — what
 * the pack drawer renders beside the dimension inputs.
 *
 * `volumetric()` is the completeness gate rather than a hand-rolled check: it
 * is already the section's definition of "these three numbers are usable",
 * and a triple it rejects is a triple there is nothing honest to draw for.
 * The gate checks `== null`, not falsiness — `volumetric()` returns a real
 * number that is legitimately `0` for any carton under 25 cm³ (e.g. a fully
 * entered `[2, 3, 4]`), and `0` is falsy but not incomplete.
 *
 * @param {number[]|{l:number,b:number,h:number}} dims  length, breadth, height in centimetres
 */
export default function CartonFigure({dims}) {
  const triple = toTriple(dims)
  const vol = volumetric(triple)

  if (vol == null) {
    return (
      <Box sx={{display: 'grid', placeItems: 'center', minHeight: 150, p: 1.5, textAlign: 'center'}}>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, maxWidth: '34ch'}}>
          Enter length, breadth and height to see the carton and its volumetric weight.
        </Typography>
      </Box>
    )
  }

  const nums = triple.map(Number)
  const big = Math.max(nums[0], nums[1], nums[2])
  const max = Math.max(MIN_PX, Math.min(CARTON_PX, (CARTON_PX * big) / REF_CM))
  const {w, ht} = frame(nums, max)
  const height = Math.min(ht, max)

  return (
    <Box sx={{display: 'grid', gap: 0.75, justifyItems: 'center'}}>
      {/* The drawing sits in a fixed-height well so the caption does not walk
          up and down the panel on every keystroke. The inner box is sized from
          the same geometry the SVG lays out with — hand the SVG a box of the
          wrong aspect and it fits itself to that box instead, which is how a
          longer carton ends up rendering smaller than a shorter one. */}
      <Box sx={{display: 'grid', placeItems: 'center', width: '100%', minHeight: 172}}>
        <Box sx={{width: Math.round((w / ht) * height), height: Math.round(height), flex: '0 0 auto'}}>
          <MeasuredCarton dims={nums} max={max} />
        </Box>
      </Box>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, textAlign: 'center', maxWidth: '34ch'}}>
        {big > REF_CM
          ? `Drawn to scale. This carton is past the ${REF_CM} cm reference, so the outline holds its shape but stops growing.`
          : big < MIN_DRAWN_CM
          ? `Drawn to scale down to about ${MIN_DRAWN_CM.toFixed(1)} cm, so the outline holds its shape but stops shrinking below that.`
          : `Drawn to scale against a ${REF_CM} cm reference, so the outline grows and shrinks with the numbers you type.`}
      </Typography>
    </Box>
  )
}
